import { useState, useEffect, useCallback } from 'react';
import { Lock, KeyRound, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTenant } from '@/contexts/TenantContext';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/lib/logger';

interface LockScreenProps {
  userId: string;
  onUnlock: () => void;
}

export function LockScreen({ userId, onUnlock }: LockScreenProps) {
  const [pin, setPin] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { toast } = useToast();
  const { tenant } = useTenant();
  const { profile } = useAuth();

  const verifyPin = useCallback(async (pinToVerify: string) => {
    if (pinToVerify.length !== 4) return;

    setIsLoading(true);
    try {
      const { data, error: rpcError } = await supabase
        .rpc('verify_user_pin', { p_user_id: userId, p_pin: pinToVerify });

      if (rpcError) throw rpcError;

      if (data === true) {
        onUnlock();
        toast({ title: 'Ξεκλειδώθηκε', description: 'Καλώς ήρθατε πίσω!' });
      } else {
        setError('Λάθος PIN');
        setPin('');
      }
    } catch (err) {
      logger.error('PIN verification failed', { error: err, userId, component: 'LockScreen' });
      setError('Σφάλμα επαλήθευσης');
    } finally {
      setIsLoading(false);
    }
  }, [userId, onUnlock, toast]);

  const handlePinChange = (value: string) => {
    // Only allow digits
    const digits = value.replace(/\D/g, '').slice(0, 4);
    setPin(digits);
    setError('');
    
    // Auto-unlock when 4 digits are entered
    if (digits.length === 4) {
      verifyPin(digits);
    }
  };

  const handleManualVerify = () => {
    if (pin.length === 4) {
      verifyPin(pin);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-background flex flex-col items-center justify-center p-4">
      <div className="flex flex-col items-center gap-6 w-full max-w-sm">
        {/* Tenant Logo & Name */}
        <div className="flex flex-col items-center gap-4 mb-2">
          {tenant?.logo_url ? (
            <div className="w-24 h-24 bg-primary/5 rounded-2xl flex items-center justify-center overflow-hidden border border-border/50 shadow-sm">
              <img src={tenant.logo_url} alt={tenant.name} className="max-w-full max-h-full object-contain p-2" />
            </div>
          ) : (
            <img src={import.meta.env.VITE_DEFAULT_FAVICON || '/enviflow_favicon.webp'} alt="EnviFlow" className="w-16 h-16 object-contain" />
          )}
          <h2 className="text-xl font-bold tracking-tight text-primary">
            {tenant?.name || import.meta.env.VITE_BRANDING_NAME || 'EnviFlow CRM'}
          </h2>
        </div>

        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
          <Lock className="w-10 h-10 text-primary" />
        </div>
        
        <div className="text-center">
          <h1 className="text-2xl font-bold">Κλειδωμένο</h1>
          <p className="text-muted-foreground mt-1">
            Εισάγετε το PIN για να συνεχίσετε
          </p>
          {profile?.lock_timeout && (
            <p className="text-[10px] text-muted-foreground mt-2 italic">
              Αυτόματο κλείδωμα στα {profile.lock_timeout} δευτερόλεπτα
            </p>
          )}
        </div>

        <div className="flex flex-col items-center gap-4 w-full">
          <div className="relative w-full">
            <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              placeholder="••••"
              value={pin}
              onChange={(e) => handlePinChange(e.target.value)}
              className="pl-10 text-center text-2xl tracking-[0.5em] font-mono"
              autoFocus
              disabled={isLoading}
              onKeyDown={(e) => e.key === 'Enter' && handleManualVerify()}
            />
          </div>

          <Button 
            className="w-full h-12 text-lg" 
            disabled={pin.length !== 4 || isLoading}
            onClick={handleManualVerify}
            style={tenant?.primary_color ? { backgroundColor: tenant.primary_color } : {}}
          >
            {isLoading ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Επαλήθευση...</>
            ) : (
              <><CheckCircle2 className="w-5 h-5 mr-2" /> Επαλήθευση</>
            )}
          </Button>

          {error && (
            <p className="text-sm text-destructive font-medium">{error}</p>
          )}
        </div>
      </div>

      {/* Promotion Link */}
      {(tenant?.show_branding !== false) && (
        <div className="fixed bottom-8 flex items-center gap-2 bg-card/50 backdrop-blur-sm px-4 py-2 rounded-full border border-border/50 text-xs text-muted-foreground transition-all">
          <span>Δημιουργήθηκε από</span>
          <a
            href={import.meta.env.VITE_BRANDING_URL || 'https://enviflow.gr'}
            target="_blank"
            rel="noopener noreferrer"
            className="font-bold text-primary hover:underline"
          >
            EnviFlow CRM
          </a>
        </div>
      )}
    </div>
  );
}
